import DOMPurify from 'dompurify';

// Hosts SunEditor produces <iframe> embeds for when inserting a video by URL.
const ALLOWED_IFRAME_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'player.vimeo.com',
];

const MEDIA_SELECTOR = 'img, video, iframe, audio, table, hr';

let purifier: ReturnType<typeof DOMPurify> | null = null;

function getPurifier(): ReturnType<typeof DOMPurify> | null {
  if (purifier) return purifier;
  if (typeof window === 'undefined') return null;

  // Dedicated instance so these hooks don't leak into other DOMPurify users.
  purifier = DOMPurify(window);

  purifier.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const el = node as Element;
    if (!isAllowedIframeSrc(el.getAttribute('src'))) {
      el.parentNode?.removeChild(el);
    }
  });

  purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return purifier;
}

function isAllowedIframeSrc(src: string | null): boolean {
  if (!src) return false;
  try {
    const url = new URL(src, window.location.href);
    return url.protocol === 'https:' && ALLOWED_IFRAME_HOSTS.includes(url.hostname);
  } catch {
    return false;
  }
}

/**
 * Strips scripts, event handlers and dangerous URLs from rich-text HTML while keeping
 * the formatting produced by SunEditor (inline styles, fonts, lists, tables, images, video embeds).
 */
export function sanitizeRichHtml(html: string | null | undefined): string {
  if (!html) return '';
  const instance = getPurifier();
  if (!instance?.isSupported) return html;

  return instance.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
    FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'object', 'embed'],
  }) as string;
}

export function plainTextToHtml(text: string | null | undefined): string {
  if (!text || !text.trim()) return '';
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}


export function richHtmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '').trim();

  const template = document.createElement('template');
  template.innerHTML = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, '$&\n\n');
  return (template.content.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * True when rich-text HTML has no visible text and no media, e.g. SunEditor's empty `<p><br></p>`.
 */
export function isRichHtmlEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  if (typeof document === 'undefined') return !html.replace(/<[^>]*>/g, '').trim();

  const template = document.createElement('template');
  template.innerHTML = html;
  const content = template.content;

  if (content.querySelector(MEDIA_SELECTOR)) return false;
  return !(content.textContent || '').replace(/[\s\u00a0\u200b]+/g, '');
}
